import classNames from 'classnames';
import Avatar from 'components/Avatar';
import DurationFormat from 'components/DurationFormat';
import DiagnosticResourceId from 'components/dev-tools/DiagnosticResourceId';
import { clamp } from 'lodash-es';
import { DateTime } from 'luxon';
import React, { useCallback, useMemo, useState } from 'react';
import { getHistoricCapacityByDate } from 'redux/selectors/utils/capacities';
import type { RawCapacity } from 'types/summaries';
import { ucFirst } from 'utils/text-helper';
import type { GanttUserRow } from '../gantt-types';

import styles from './LeftHeader.module.scss';

interface Props {
  row: GanttUserRow;
  isLastRow: boolean;
  style: React.CSSProperties;
  capacityHistory: Array<RawCapacity>;
  showAvailability: boolean;
  rowHeight: number;
  scrollTop: number;
}

const VERTICAL_PADDING = 8;
const TODAY = DateTime.now().toISODate();
const LeftUserRow: React.FC<Props> = ({
  row,
  isLastRow,
  style,
  capacityHistory,
  showAvailability,
  rowHeight,
  scrollTop,
}: Props) => {
  const baseCapacity = useMemo(
    () => getHistoricCapacityByDate(capacityHistory, TODAY),
    [capacityHistory]
  );

  // Make avatar sticky during scroll
  const [cellHeight, setCellHeight] = useState<number>(0);
  const cellRowRef = useCallback((element: HTMLDivElement | null) => {
    setCellHeight(element?.offsetHeight ?? 0);
  }, []);
  const top = clamp(
    scrollTop - Number(style.top!),
    0,
    rowHeight - cellHeight - VERTICAL_PADDING * 2
  );

  return (
    <div
      style={style}
      className={classNames(styles.leftHeaderRow, {
        [styles.lastRow]: isLastRow,
      })}
    >
      <div className={styles.leftHeaderCell}>
        <div className={styles.user} ref={cellRowRef} style={{ top }}>
          {row.userId == null ? (
            <div className={styles.avatarPlaceholder} />
          ) : (
            <Avatar
              width={48}
              height={48}
              className={styles.avatar}
              shape="rounded"
              size="large"
              src={row.avatar}
              name={row.name}
              fallbackToInitials
              tooltipPosition="right"
            />
          )}
          <div className={styles.text}>
            <div className={classNames(styles.name, 'fs-mask')}>
              <DiagnosticResourceId resource={row.type} id={row.userId} />
              {row.name}
            </div>
            {row.userRole != null && (
              <div className={styles.capacityAndRole}>
                <span className={classNames(styles.role, 'fs-mask')}>{ucFirst(row.userRole)}</span>
                {showAvailability && (
                  <>
                    ,{' '}
                    <DurationFormat
                      trim="both"
                      value={baseCapacity?.weekly_capacity}
                      valueType="hours"
                      format="h[h] mm[m]"
                    />
                    /wk
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
export default LeftUserRow;
